(ns app.front.view.group.admin.fees.payment.payment-core
  (:require [app.front.ui :as ui]
            [app.front.ui.table :as ui-table]
            [app.front.ui.text-editor :as text-editor]
            [app.front.util :refer [for-all]]
            [app.front.view.group.admin.feature-toggle :as feature-toggle]
            [app.front.view.group.admin.fees.integration.toggle :as integration-toggle]
            [app.front.view.group.admin.fees.payment.payment-events :as payment-events]
            [app.front.view.group.admin.fees.payment.payment-subs :as payment-subs]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [reagent.core :as r]))


(defn online-payment-toggle
  [tr authority-group-id payment-method]
  (cc/toggle {:key        "online-payment"
              :id         "online-payment"
              :label-text (tr [:fees/enable-online-payment])
              :toggled    (and (not (:payment-method/disabled? payment-method))
                               (some? payment-method))
              :on-toggle  (fn [checked]
                            (if checked
                              (>evt [::payment-events/enable-online-payment authority-group-id])
                              (>evt [::payment-events/disable-online-payment authority-group-id])))}))

(defn bill-pdf-generation-toggle
  [tr authority-group-id]
  (let [feature  :group.feature/generate-bill-pdf
        enabled? (<sub [::gas/has-feature? feature])]
    [:<>
     (cc/toggle {:key        "bill-pdf-toggle"
                 :id         "bill-pdf-toggle"
                 :label-text (tr [:fees/enable-bill-pdf-generation])
                 :toggled    enabled?
                 :on-toggle  (fn [checked]
                               (>evt [::feature-toggle/toggle-group-feature
                                      authority-group-id
                                      feature
                                      checked]))})
     (when enabled?
       (cc/toggle {:id         "draft-bill-pdf-toggle"
                   :label-text (tr [:group.feature/generate-bill-pdf-for-draft])
                   :toggled    (<sub [::gas/has-feature? :group.feature/generate-bill-pdf-for-draft])
                   :on-toggle  (fn [checked]
                                 (>evt [::feature-toggle/toggle-group-feature
                                        authority-group-id
                                        :group.feature/generate-bill-pdf-for-draft
                                        checked]))}))]))

(defn payment-instructions
  "The payment instructions are included in the email with the bill"
  [tr authority-group-id]
  (let [instructions (<sub [::payment-subs/payment-instructions authority-group-id])]
    [text-editor/editor
     {:tr           tr
      :always-show? true
      :title        [:h3.mb-0 (tr [:fees/payment-instructions])]
      :contents     [{:label :fees/payment-instructions
                      :value instructions}]
      :on-save      #(>evt [::payment-events/update-payment-instructions authority-group-id %])}]))


(defn payment-detail-table [tr group-id]
  (let [details                (<sub [::payment-subs/payment-details group-id])
        cheque-number-enabled? (<sub [::ff/enabled? :feature-flag.id/add-cheque-number])]
    #_{:clj-kondo/ignore [:deprecated-var]}
    [ui-table/table {:tr    tr
                     :class "payment-detail-table"}
     [[:th (tr [:payment-detail/name])]
      (when cheque-number-enabled? [:th (tr [:payment-detail/cheque?])])
      [:th ""]]

     (for-all [{:db/keys [id] :as detail} details]
       {:key  id
        :cols [{:content (-> detail :payment-detail/name)}

               (when cheque-number-enabled?
                 {:content (cc/checkbox {:id         (str "check-cheque-" id)
                                         :checked    (true? (:payment-detail/cheque? detail))
                                         :on-change  #(>evt [::payment-events/update-payment-detail group-id detail])
                                         :label-text (tr [""])})})
               {:opts    {:class-name "table-row__actions"}
                :content [:<>
                          [ui/modal-confirmation-button {:tr                tr
                                                         :id                "delete-detail"
                                                         :modal-title       (tr [:payment-detail/delete])
                                                         :trigger-icon-opts {:icon        i/trash
                                                                             :description (tr [:payment-detail/delete])
                                                                             :class-name  "cursor-pointer"}
                                                         :on-yes-click      #(>evt [::payment-events/delete-payment-detail group-id (:db/id detail)])}
                           (tr [:payment-detail/delete-confirm])]]}]})]))


(defn payment-detail-editor [tr group-id]
  (r/with-let [*editing?    (r/atom false)
               initial-data {:payment-detail/name ""}
               *detail-data (r/atom initial-data)]
    [:div
     (when-not @*editing?
       (cc/button
         {:kind             :tertiary
          :icon             i/plus
          :icon-description (tr [:payment-detail/add])
          :on-click         #(reset! *editing? true)}
         (tr [:payment-detail/add])))
     (when @*editing?
       [:div.bx--row.animation-appear-slide-down
        [:h4.bx--col-xs-12.mb-3
         (tr [:payment-detail/add])]
        [:div.bx--col-xs-12.bx--col-md-6.bx--col-lg-4
         [ui/text-input {:placeholder (tr [:payment-detail/name])
                         :label-text  (tr [:payment-detail/name])
                         :on-change   #(swap! *detail-data assoc :payment-detail/name (-> % .-target .-value))}]]
        [:div.bx--col-xs-12.bx--col-md-6.bx--col-lg-8.flex.justify-end.md:justify-start.gap-2.items-end
         (cc/button
           {:kind     :tertiary
            :size     :field
            :on-click #(reset! *editing? false)}
           (tr [:cancel]))
         (cc/button
           {:kind     :primary
            :size     :field
            :disabled (->> @*detail-data
                           vals
                           (some str/blank?)
                           boolean)
            :on-click (fn [_]
                        (>evt [::payment-events/save-payment-detail group-id @*detail-data])
                        (reset! *editing? false)
                        (reset! *detail-data initial-data))}
           (tr [:payment-detail/save]))]])]))


(defn payment-details [tr authority-group-id]
  [:<>
   [:h3.m-0 (tr [:payment-detail/payment-details])]
   [payment-detail-table tr authority-group-id]
   [payment-detail-editor tr authority-group-id]])


(defn view
  "Online payment configuration for authority admins.
  The config is in the same page as the fees config and applies to the same authority group."
  []
  (let [authority-group-id (<sub [::gas/group-id])
        payment-method     (<sub [::payment-subs/online-payment-method authority-group-id])
        tr                 (<sub [::gas/group-tr])]
    [:div.flex.flex-col.gap-4
     [online-payment-toggle tr authority-group-id payment-method]
     [bill-pdf-generation-toggle tr authority-group-id]
     [integration-toggle/view]
     [payment-instructions tr authority-group-id]
     [payment-details tr authority-group-id]]))
